import React, { useState } from 'react';
import { Popover } from '@mui/material';
import { CalendarEvent } from '../../../../types/calendar/calendarTypes';
import useTranslate from '../../../../customHooks/useTranslate';
import { useSearchParams } from '../../../../customHooks/useSearchParams';
import { formatDate } from '../../../../utils/formatDates';
import DotIcon from '../../../components/DotIcon';
import Label from '../../yourProperties/Label';
import IconButton from '../../../../muiComponents/IconButton';
import SectionTitle from '../../yourProperties/Title';
import Tooltip from '../../../../muiComponents/Tooltip';
import Grid from '../../../../muiComponents/Grid';
import GridChild from '../../../../muiComponents/GridChild';
import Icon from '../../../../muiComponents/Icon';
import Title from '../../../components/Title';
import {
    doubleMD_ColumnGrid,
    gridSpacing30,
    gridSpacing20,
} from '../../../../utils/commonVariables/grid';
import { commonIcons } from '../../../../utils/commonVariables/commonIcons';
import routes from '../../../../utils/commonVariables/routes';
import { IStopSaleListState } from '../../../../types/stopSale/stopSalesTypes';

interface EventProps {
    event: CalendarEvent<IStopSaleListState>;
    idHoveredEvent: string | null;
    hoverEvent: (eventId: string) => void;
    unhoverEvent: () => void;
}

const StopSaleEvent = ({
    event,
    idHoveredEvent,
    hoverEvent,
    unhoverEvent,
}: EventProps) => {
    const [anchor, setAnchor] = useState<HTMLElement | null>(null);
    const translate = useTranslate();
    const {
        Id,
        Enabled,
        FromDate,
        ToDate,
        IdSupplierHotel,
        IdSupplierHotelRoomType,
        IdSupplierHotelMealPlan,
        IdSupplierHotelRatePlan,
        SupplierHotelRoomType,
        SupplierHotelMealPlan,
        SupplierHotelRatePlan,
        SupplierHotel,
    } = event.extendedProps;

    const { setSearchParam } = useSearchParams({ base64: true });

    const handleShowDetails = (idSupplement: string) => {
        setAnchor(null);
        setSearchParam('id', idSupplement);
    };

    const Name = `${FromDate ? formatDate(FromDate) : '--/--/----'} - ${
        ToDate ? formatDate(ToDate) : '--/--/----'
    }`;

    return (
        <>
            <div
                className={`overflow-hidden flex flex-between gap-5 flex-align-center border-radius-4 cursor-pointer padding-4 ${
                    idHoveredEvent === event.id
                        ? 'fc__hovered-event'
                        : 'border-color'
                }`}
                style={{ height: '30px', maxWidth: '100%' }}
                onClick={() => {
                    handleShowDetails(Id);
                }}
                onMouseEnter={() => hoverEvent(event.id)}
                onMouseLeave={unhoverEvent}
            >
                <div className='flex gap-5 flex-align-center overflow-hidden'>
                    <DotIcon truthy={Enabled} falsy={!Enabled} />

                    <Label text={Name} translated={false} />
                </div>
                <IconButton
                    iconName={anchor ? 'VisibilityOff' : 'Visibility'}
                    onClick={(
                        iconEvent: React.MouseEvent<
                            HTMLButtonElement,
                            MouseEvent
                        >
                    ) => {
                        iconEvent.stopPropagation();
                        setAnchor(anchor ? null : iconEvent.currentTarget);
                    }}
                    style={{ padding: '4px' }}
                    iconProps={{ style: { fontSize: '16px' } }}
                />
            </div>
            <Popover
                open={Boolean(anchor)}
                anchorEl={anchor}
                onClose={() => setAnchor(null)}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
                transformOrigin={{ vertical: 'top', horizontal: 'left' }}
                sx={{
                    paper: {
                        borderRadius: '16px',
                    },
                }}
            >
                <div
                    style={{ width: '600px' }}
                    className='flex-column gap-20 padding20'
                >
                    <div className='flex flex-between flex-align-baseline padding-inline15 gap-10'>
                        <SectionTitle
                            className='text-secondary-color multiline-overflow__2'
                            text={Name}
                            translated={false}
                        />
                        <div className='flex gap-20 flex-align-center'>
                            <Tooltip
                                title={translate(
                                    Enabled ? 'enabled' : 'disabled'
                                )}
                            >
                                <span>
                                    <DotIcon
                                        truthy={Enabled}
                                        falsy={!Enabled}
                                    />
                                </span>
                            </Tooltip>
                        </div>
                    </div>

                    <Grid
                        style={{ width: 'auto', margin: 0 }}
                        className='border-color-block'
                        columnSpacing={gridSpacing30}
                        rowSpacing={gridSpacing20}
                    >
                        <GridChild gridProps={doubleMD_ColumnGrid}>
                            <div className='flex-column gap-5'>
                                <div className='flex flex-align-center gap-5 text-overflow-ellipsis'>
                                    <Icon
                                        iconName={commonIcons.HOTEL}
                                        className='text-secondary-700'
                                    />
                                    <Title className='text-capitalize'>
                                        {translate('hotel')}
                                    </Title>
                                </div>
                                <Tooltip titleText='navigate_to_hotel_detail'>
                                    <p
                                        className='link-onhover cursor-pointer '
                                        onClick={() => {
                                            const route = `${routes.propertyDetail(
                                                IdSupplierHotel
                                            )}`;
                                            window.open(route, route);
                                        }}
                                    >
                                        {SupplierHotel}
                                    </p>
                                </Tooltip>
                            </div>
                        </GridChild>
                        {IdSupplierHotelRoomType ? (
                            <GridChild gridProps={doubleMD_ColumnGrid}>
                                <div className='flex-column gap-5'>
                                    <div className='flex flex-align-center gap-5 text-overflow-ellipsis'>
                                        <Icon
                                            iconName='Bed'
                                            className='text-secondary-700'
                                        />
                                        <Title>{translate('room')}</Title>
                                    </div>
                                    <p>
                                        {SupplierHotelRoomType ||
                                            translate('not_specified')}
                                    </p>
                                </div>
                            </GridChild>
                        ) : null}
                        {IdSupplierHotelRatePlan ? (
                            <GridChild gridProps={doubleMD_ColumnGrid}>
                                <div className='flex-column gap-5'>
                                    <div className='flex flex-align-center gap-5 text-overflow-ellipsis'>
                                        <Icon
                                            iconName='SellOutlined'
                                            className='text-secondary-700'
                                        />
                                        <Title className='text-capitalize'>
                                            {translate('rate_plan')}
                                        </Title>
                                    </div>
                                    <p>
                                        {SupplierHotelRatePlan ||
                                            translate('not_specified')}
                                    </p>
                                </div>
                            </GridChild>
                        ) : null}
                        {IdSupplierHotelMealPlan ? (
                            <GridChild gridProps={doubleMD_ColumnGrid}>
                                <div className='flex-column gap-5'>
                                    <div className='flex flex-align-center gap-5 text-overflow-ellipsis'>
                                        <Icon
                                            iconName='RoomService'
                                            className='text-secondary-700'
                                        />
                                        <Title className='text-capitalize'>
                                            {translate('meal_plan')}
                                        </Title>
                                    </div>
                                    <p>
                                        {SupplierHotelMealPlan ||
                                            translate('not_specified')}
                                    </p>
                                </div>
                            </GridChild>
                        ) : null}
                    </Grid>

                    <div
                        className='padding-inline15 gap-10 flex flex-baseline text-primary-400 cursor-pointer'
                        onClick={() => handleShowDetails(Id)}
                    >
                        <Icon iconName='Description' />
                        {translate('go_to_stop_sale_detail')}
                    </div>
                </div>
            </Popover>
        </>
    );
};

export default StopSaleEvent;
